/**
 * Pure objective-type helpers — no React, no HTTP, no path aliases — so the
 * normalisation and label-resolution logic stays unit-testable in isolation
 * from the react-query hook in ``objectiveTypes.ts``.
 */

export interface ObjectiveType {
  id: string;
  key: string;
  label: string;
}

export type ObjectiveTypesResponse =
  | ObjectiveType[]
  | { objectiveTypes: ObjectiveType[] };

/**
 * Normalise the two accepted response shapes into a clean list. Entries
 * missing a ``key`` are dropped; ``label`` falls back to the key and ``id``
 * falls back to the key.
 */
export function normalizeObjectiveTypes(
  data: ObjectiveTypesResponse | null | undefined
): ObjectiveType[] {
  if (!data) return [];
  const raw = Array.isArray(data) ? data : data.objectiveTypes;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((t): t is ObjectiveType => Boolean(t && t.key))
    .map((t) => ({ id: t.id || t.key, key: t.key, label: t.label || t.key }));
}

/**
 * Resolve a stored objective key to a display label. Unknown keys fall back
 * to the raw key so the UI never renders an empty objective.
 */
export function resolveObjectiveLabel(
  key: string | null | undefined,
  types: ObjectiveType[]
): string | null {
  if (!key) return null;
  const match = types.find((t) => t.key === key);
  return match ? match.label : key;
}
